import * as React from 'react'
import { toast } from 'react-toastify'
import type * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'

interface OpenExportFile {
  readonly isExporting: boolean
  readonly openExportFile: (load: () => Promise<Api.ExportFile>) => Promise<void>
}

/**
 * Requests an export and opens its `downloadUrl` in a new tab. The link expires after an hour, so every
 * click requests a fresh one; the backend's error text is already translated and is shown as is.
 */
export function useOpenExportFile(): OpenExportFile {
  const [isExporting, setIsExporting] = React.useState(false)

  const openExportFile = React.useCallback(
    async (load: () => Promise<Api.ExportFile>) => {
      if (isExporting) return
      setIsExporting(true)
      try {
        const file = await load()
        window.open(file.downloadUrl, '_blank', 'noopener,noreferrer')
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      } finally {
        setIsExporting(false)
      }
    },
    [isExporting]
  )

  return { isExporting, openExportFile }
}
